/**
 * API client for the OMNΛS Exchange surface (backend #23 identity/passport,
 * #24 demand profile + matching).
 *
 * Every route lives under the `/exchange` router, which carries the
 * `marketplace_exchange` TENANT ENTITLEMENT as its only router-level
 * dependency, plus a global `OMNAS_EXCHANGE_API_ENABLED` flag checked in the
 * endpoint body. Three distinct refusals therefore reach the UI and must
 * never be collapsed into one "unavailable":
 *
 *   403 {code: tenant_not_entitled}  — this tenant may not use the surface
 *   403 "Insufficient permissions"   — this user's role may not (plain string)
 *   503 {code: not_enabled}          — the platform flag is off
 *
 * Note the deliberate ordering difference the backend documents:
 *   identity endpoints  → flag checked BEFORE the record lookup (503 wins)
 *   project/package     → record guard BEFORE the flag (404 wins), so an
 *                         unknown id never reveals whether the API is on.
 */
import apiClient from './api';

/**
 * Contract versions this client is written against. There are SIX, not one —
 * each endpoint family carries its own, and treating them as a single version
 * would mismatch on the next backend change.
 */
export const EXCHANGE_CONTRACTS = {
  identity: 'exchange-identity-1',
  passport: 'provider-passport-1',
  demandProfile: 'project-demand-profile-1',
  matching: 'exchange-demand-matching-1',
  shortlist: 'exchange-shortlist-1',
  relationship: 'exchange-relationship-1',
  rfq: 'exchange-rfq-1',
};

// The shortlist state machine and event vocabulary live in
// features/exchange/exchangePresentation.js — they are domain vocabulary, not
// transport, and that module is dependency-free so tests can drive them.

const exchangeApi = {
  /**
   * Network organizations (identity anchors) visible to this tenant.
   * `q` matches display name / normalized name; LIKE metacharacters are
   * escaped server-side. No pagination exists — the server caps at 200 and
   * reports `truncated`.
   */
  listProviders: async (q) => {
    const { data } = await apiClient.get('/exchange/providers', {
      params: q && q.trim() ? { q: q.trim() } : {},
    });
    return data;
  },

  /** Provider Passport read model for one network organization. */
  getProvider: async (organizationId) => {
    const { data } = await apiClient.get(`/exchange/providers/${organizationId}`);
    return data;
  },

  /**
   * The derived demand profile for one package — what this scope actually
   * requires, with per-field provenance. Also inlined inside the matches
   * response, so callers that need both should prefer `matches`.
   */
  demandProfile: async (projectId, packageId) => {
    const { data } = await apiClient.get(
      `/exchange/projects/${projectId}/packages/${packageId}/demand-profile`
    );
    return data;
  },

  /**
   * Candidate providers for one package: eligibility gates, operational-fit
   * score, and relationship context kept separate by the backend. Includes
   * the full demand profile under `demandProfile`.
   */
  matches: async (projectId, packageId) => {
    const { data } = await apiClient.get(
      `/exchange/projects/${projectId}/packages/${packageId}/matches`
    );
    return data;
  },

  /**
   * Scan this tenant's company / vendor / subcontractor records and propose
   * identity links. Takes no body and no parameters by design: automation may
   * only ever write PROPOSED_MATCH, and there is no way to ask it for any
   * other state. Confirming or disputing is a separate human decision.
   *
   * The response deliberately omits `createdOrganizations` (a cross-tenant
   * existence probe), and its `scanTruncated` / `fuzzyTruncated` flags must be
   * surfaced — a bounded run that looks complete is the failure this guards.
   */
  proposeIdentityLinks: async () => {
    const { data } = await apiClient.post('/exchange/identity/propose');
    return data;
  },

  /**
   * Decide one proposed identity link.
   * decision ∈ CONFIRMED | DISPUTED | UNRESOLVED (PROPOSED_MATCH is not
   * sendable). DISPUTED requires a non-blank note; DISPUTED → CONFIRMED is
   * forbidden by the backend and must go through UNRESOLVED.
   */
  decideIdentityLink: async (linkId, decision, note) => {
    const { data } = await apiClient.post(`/exchange/identity/links/${linkId}/decide`, {
      decision,
      ...(note ? { note } : {}),
    });
    return data;
  },

  // -------------------------------------------------------------------------
  // Shortlist (backend #25) — automation proposes, humans decide
  // -------------------------------------------------------------------------

  /**
   * Run matching and persist PROPOSED shortlist entries. No body, no
   * parameters: the service has no path to any state but PROPOSED, and
   * eligibility FAIL candidates are never proposed at all.
   *
   * Additive-idempotent — re-running counts existing candidates rather than
   * duplicating them, and a race answers 409 shortlist_proposal_conflict.
   */
  proposeShortlist: async (projectId, packageId) => {
    const { data } = await apiClient.post(
      `/exchange/projects/${projectId}/packages/${packageId}/shortlist/propose`
    );
    return data;
  },

  /**
   * The shortlist read model. `state` is membership ONLY; the downstream
   * lifecycle (QUOTED / EVALUATED / RECOMMENDED / AUTHORIZED / ASSIGNED)
   * arrives under each entry's `derived` block, joined at read time from the
   * tables that own it and never stored on the entry.
   */
  getShortlist: async (projectId, packageId) => {
    const { data } = await apiClient.get(
      `/exchange/projects/${projectId}/packages/${packageId}/shortlist`
    );
    return data;
  },

  /**
   * One human membership decision. `note` is required by the backend for
   * every decision, not only for exclusions.
   */
  decideShortlistEntry: async (entryId, decision, note) => {
    const { data } = await apiClient.post(`/exchange/shortlist/entries/${entryId}/decide`, {
      decision,
      note,
    });
    return data;
  },

  // -------------------------------------------------------------------------
  // Relationship (backend #25) — TENANT-PRIVATE human statements
  // -------------------------------------------------------------------------

  /**
   * Append one human relationship statement. Append-only: there is no update
   * or delete surface, and a changed stance is a new statement. Derived
   * events (INVITED, BID, AWARDED…) are refused with 422 — they live in the
   * records that own them.
   *
   * The note never leaves the recording tenant.
   */
  recordRelationshipEvent: async (body) => {
    const { data } = await apiClient.post('/exchange/relationships/events', body);
    return data;
  },

  /**
   * Relationship context for one network organization: interaction facts,
   * named derivations with stated denominators, and this tenant's own stored
   * statements. Absence is UNKNOWN — never poor performance.
   */
  getProviderRelationship: async (organizationId) => {
    const { data } = await apiClient.get(`/exchange/providers/${organizationId}/relationship`);
    return data;
  },

  /**
   * The provider outcome ledger (`provider-outcome-2`) for one network
   * organization: what this tenant recorded about the firm, and how
   * defensibly each fact attaches to it.
   *
   * Same three ANDed layers as the passport — `marketplace_exchange`
   * entitlement, one of five read roles, `OMNAS_EXCHANGE_API_ENABLED` — and
   * no capability check of its own. There is no request body, no query
   * parameter, and no write path: `readOnly: true` on every response is a
   * statement about the whole module.
   *
   * A `404` means this tenant holds NO identity link for the organization,
   * so the ledger is unavailable rather than empty — route to identity
   * resolution, not to an empty state. There is deliberately no `403` for a
   * cross-tenant organization: it answers the identical `404`, so the
   * endpoint cannot be used to probe another tenant's anchors.
   */
  getProviderOutcomes: async (organizationId) => {
    const { data } = await apiClient.get(`/exchange/providers/${organizationId}/outcomes`);
    return data;
  },

  // -------------------------------------------------------------------------
  // RFQ handoff (backend #26, contract exchange-rfq-1)
  //
  // The commercial bridge between "who could bid" and "who is bidding".
  // NOTHING ON THIS SURFACE SENDS ANYTHING. There is no external send path in
  // the backend at all: preparing writes an internal record, and authorizing
  // records a human's permission for that provider to enter the RFQ workflow.
  // Every response repeats that structurally in four booleans, and the UI
  // renders those rather than asserting a send.
  //
  // Availability is THREE ANDed layers, all fail-closed server-side:
  //   flags        OMNAS_EXCHANGE_API_ENABLED AND OMNAS_RFQ_API_ENABLED → 503
  //   entitlement  marketplace_exchange (router) AND rfq (endpoint)     → 403
  //   authorization per endpoint; authorize is an INTERSECTION of the
  //                `rfq.publish` capability and four decision roles, so an
  //                estimator holding the capability still cannot authorize.
  // -------------------------------------------------------------------------

  /**
   * Prepare one shortlisted provider for RFQ. One entry per call.
   *
   * The backend resolves the commercial identity ONCE here and the bidder key
   * never moves afterwards, which is what makes the later quote join exact.
   * Idempotent on (tenant, package, shortlistEntry): a retry after a dropped
   * connection returns the existing release with `created: false` and the
   * identity AS PREPARED — never re-resolved — so a retry can never advertise
   * a key the release does not carry. The client needs no idempotency key of
   * its own and must not invent one.
   */
  prepareRfq: async (projectId, packageId, shortlistEntryId) => {
    const { data } = await apiClient.post(
      `/exchange/projects/${projectId}/packages/${packageId}/rfq/prepare`,
      { shortlistEntryId }
    );
    return data;
  },

  /**
   * Record a human's authorization to release one prepared provider into the
   * RFQ workflow.
   *
   * `confirmed` must be sent as true and must reflect a deliberate human act —
   * the backend answers 428 confirmation_required otherwise, and defaulting it
   * in the client would defeat the only thing this field exists for.
   *
   * Replay of an already-RELEASED row answers 200 with `changed: false`. That
   * is not an error and not a second authorization.
   */
  authorizeRfqRelease: async (releaseId, { confirmed, note } = {}) => {
    const { data } = await apiClient.post(`/exchange/rfq/releases/${releaseId}/authorize`, {
      confirmed: confirmed === true,
      ...(note ? { note } : {}),
    });
    return data;
  },

  /**
   * Every release on one package, with two blocks the backend derives at read
   * time and never stores: `shortlist` (has the human decision drifted since
   * preparation?) and `downstream` (has this SPECIFIC provider responded?).
   *
   * `downstream` is per bidder key, not per package — another provider's quote
   * can never make this one look like it answered. It is derived per request
   * with no subscription, so poll while the user is looking at it.
   */
  listRfqReleases: async (projectId, packageId) => {
    const { data } = await apiClient.get(
      `/exchange/projects/${projectId}/packages/${packageId}/rfq/releases`
    );
    return data;
  },
};

export default exchangeApi;
